import React, { Fragment, useState } from 'react';
import { useAsync } from 'react-async';
import {
  getThread,
  updateThread,
  replyToThread,
} from '../../api/services/messageService';
import Layout from '../../components/layout';
import { useParams } from 'react-router-dom';
import LayoutError from '../../components/layoutError';
import moment from 'moment';
import DataTable from '../../components/dataTable';
import { ExclamationCircleIcon } from '@heroicons/react/solid';
import MessageDisplay from '../../api/models/messageDisplay';
import Modal from '../../components/modal';
import { ResponseStatus } from '../../components/notifications';
import { useDispatch } from 'react-redux';
import { updateUnreadMessagesCount } from '../../slices/userSlice';
import { useAppSelector } from '../../app/hooks';
import DOMPurify from 'dompurify';

type Params = {
  id: string;
};

const fetchThread = async ({ id }: any) => {
  return await getThread(id);
};

const ViewThread = () => {
  const columns = React.useMemo(
    () => [
      {
        Header: 'Author',
        accessor: (m: any) =>
          m.isMyMessage
            ? 'Me'
            : m.fromUserFirstName + ', ' + m.fromUserLastName,
        extraAccessor: 'fromUserRoles',
        basicStyle: 'isRead',
        isMyMessage: 'isMyMessage',
        Cell: ({ value, column, row }: any) => {
          return (
            <div className='flex items-top'>
              {!row.original[column.basicStyle] &&
                !row.original[column.isMyMessage] && (
                  <div className='flex-shrink-0 h-5 w-5'>
                    <ExclamationCircleIcon
                      className='w-5 h-5 cursor-pointer'
                      aria-hidden='true'
                      color='red'
                    />
                  </div>
                )}
              <div
                className={
                  row.original[column.basicStyle] ||
                    row.original[column.isMyMessage]
                    ? 'ml-4'
                    : ''
                }
              >
                <div>{value}</div>
                <div className='text-sm text-gray-500'>
                  {row.original[column.extraAccessor]}
                </div>
              </div>
            </div>
          );
        },
        width: 100,
      },
      {
        Header: 'Date/Time EST',
        accessor: (m: any) => moment(m.createdOn).format('MM/DD/YYYY @h:mmA'),
        width: 50,
      },
      {
        Header: 'Message',
        accessor: 'messageText',
      },
    ],
    []
  );

  const { id } = useParams<Params>();
  const [messages, setMessages] = useState<MessageDisplay[]>([]);
  const [openMessage, setOpenMessage] = useState(false);
  const [message, setMessage] = useState('');
  const unreadMessagesCount = useAppSelector((s) => s.user.unreadMessagesCount);
  const dispatch = useDispatch();

  const updateThreadToRead = async () => {
    if (!id) {
      return Promise.reject('Missing message id.');
    }
    return await updateThread(Number(id));
  };

  const save = useAsync({
    deferFn: updateThreadToRead,
  });

  const { error, isPending } = useAsync({
    promiseFn: fetchThread,
    id,
    onResolve: (r) => {
      const sanitizedMessages = r.data.messages.map(message => ({
        ...message,
        messageText: DOMPurify.sanitize(message.messageText)
      }));

      setMessages(sanitizedMessages);
      let unreadMessages = sanitizedMessages.filter(x => !x.isRead && !x.isMyMessage)?.length;
      if (unreadMessagesCount && unreadMessages && unreadMessagesCount > 0) {
        dispatch(updateUnreadMessagesCount((unreadMessagesCount - unreadMessages) ?? 0))
      }
      save.run();
    },
  });

  const createReplyMessage = async () => {
    const sanitizedMessage = DOMPurify.sanitize(message);

    return await replyToThread({
      threadId: Number(id),
      messageText: sanitizedMessage,
    });
  };

  const addMessage = useAsync({
    deferFn: createReplyMessage,
    onResolve: (r) => {
      setMessages(r.data.messages);
    },
  });

  const handleReply = () => {
    addMessage.run();
  };

  if (error) return <LayoutError />;
  return (
    <Fragment>
      <Modal
        saveText='Send'
        allowSave={message.length !== 0}
        open={openMessage}
        onCancel={() => {
          setOpenMessage(false);
        }}
        onSave={() => {
          handleReply();
          setOpenMessage(false);
          setMessage('');
        }}
      >
        <Fragment>
          <div className='grid grid-cols-6 gap-6 mb-6'>
            <div className='col-span-6'>
              <label className='primary'>
                Message
                <span className='text-red-500 pl-1'>*</span>
              </label>
              <textarea
                minLength={1}
                maxLength={500}
                className='block primary mt-1'
                value={message}
                rows={3}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
          </div>
        </Fragment>
      </Modal>
      <Layout isLoading={isPending}>
        <Fragment>
          {addMessage.error && <ResponseStatus state={addMessage} />}
          {messages.length > 0 && (
            <div>
              <label className='text-xl mt-4'>{messages[0].threadTopic}</label>
              <br />
              <p className='text-lg'>
                {messages[0].isMyMessage
                  ? messages[0].toUserLastName +
                  ', ' +
                  messages[0].toUserFirstName
                  : messages[0].fromUserLastName +
                  ', ' +
                  messages[0].fromUserFirstName}
              </p>
              <p className='text-gray-500'>
                {messages[0].isMyMessage
                  ? messages[0].toUserRoles
                  : messages[0].fromUserRoles}
              </p>
            </div>
          )}
          <div className='mb-4 text-right'>
            <button
              onClick={() => {
                setOpenMessage(true);
              }}
            >
              Reply
            </button>
          </div>
          <DataTable
            columns={columns}
            data={messages}
            showPagination={messages.length > 30 ? true : false}
            skipFilter={true}
            getRowProps={(row: any) => ({
              className: row.original.isMyMessage
                ? 'bg-purple-200'
                : row.original.isRead
                  ? ''
                  : 'font-bold',
            })}
          />
        </Fragment>
      </Layout>
    </Fragment>
  );
};

export default ViewThread;
